/**
 * LearnerProgress — Phase Pl-E (Feb 7, 2026).
 *
 * /learn/progress is now the **test-readiness home** for pupils.
 *
 * Owner directive (Pl-E):
 *   - Progress is no longer the legacy lessons-completed dashboard.
 *   - The page must answer ONE question on open: "How ready am I for
 *     my driving test today?" — and show the next focus to close the
 *     gap.
 *   - The old, dense module dashboard is preserved at
 *     /learn/progress-legacy for anyone who still wants the granular
 *     module / streak / lesson-count view.
 *
 * Composition (top → bottom):
 *   1) TestReadyMeter — single % + breakdown chips + next focus CTA
 *   2) LearnerFrameworkStrip — Responsibility Learning System spine
 *   3) StageLadder — full 0%→100% stage view
 *   4) Quiet link to the legacy detailed dashboard
 *
 * Bottom nav stays mounted (Phase Pl-B contract). The "Progress" tab
 * is the active tab.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BookOpen, ChevronRight, Award } from "lucide-react";
import { DarkModeToggle } from "../components/DarkModeToggle";
import LearnerBottomNav from "../components/learner/LearnerBottomNav";
import TestReadyMeter from "../components/TestReadyMeter";
import LearnerFrameworkStrip from "../components/learner/LearnerFrameworkStrip";
import StageLadder from "../components/learner/StageLadder";
import { useSessionKeepAlive } from "../hooks/useSessionKeepAlive";
import { useAuth } from "../context/AuthContext";
import { LEGACY_PROGRESS_ENABLED } from "../featureFlags";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LearnerProgress() {
  const nav = useNavigate();
  const { user } = useAuth();

  // P0 keep-alive coverage on a long-dwell page (mirrors LearnerHub).
  useSessionKeepAlive(!!user, { label: "learner_progress" });

  // A3 (Feb 27, 2026 exposure batch) — quiet achievement strip at the
  // bottom of Progress. Surfaces `passport_stage_certs` (auto-issued
  // on instructor "ready" verdicts) as a calm "What you've earned"
  // section. Read-only, honest empty state, no nags. See
  // /app/memory/OPERATIONAL_EXPOSURE_MAPPING_V1.md §2.A3.
  const [stageCerts, setStageCerts] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/passport/me`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const certs = Array.isArray(r.data?.stage_certs)
          ? r.data.stage_certs
          : [];
        setStageCerts(certs);
      })
      .catch(() => {
        // Calm silence — gallery section simply omits rather than nags.
        if (!cancelled) setStageCerts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      data-testid="learner-progress-page"
      className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24"
    >
      {/* ─── Header ──────────────────────────────────────────── */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            data-testid="progress-back-to-learn"
            onClick={() => nav("/learn")}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Back to home"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white">
              Test readiness
            </h1>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
              How ready you are — and what to focus on next.
            </p>
          </div>
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 mt-5 space-y-5">
        {/* 1) Single test-ready % + next focus */}
        <TestReadyMeter />

        {/* 2) Responsibility Learning System spine */}
        <LearnerFrameworkStrip />

        {/* 3) Full 0% → 100% stage ladder */}
        <StageLadder />

        {/* A3 — What you've earned. Stage masteries auto-issued by
            instructors via stage-confirmation. Read-only, honest empty
            state. */}
        {stageCerts !== null && (
          <section
            data-testid="learner-progress-achievements"
            className="rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-700 p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 inline-flex items-center justify-center">
                <Award className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
                  What you've earned
                </h3>
                <p className="text-[11px] text-slate-500">
                  Stage milestones your instructor has confirmed.
                </p>
              </div>
            </div>

            {stageCerts.length === 0 ? (
              <p
                data-testid="learner-progress-achievements-empty"
                className="text-xs text-slate-500"
              >
                Stage milestones will appear here as your instructor
                confirms them. Nothing yet — that's normal.
              </p>
            ) : (
              <ul
                data-testid="learner-progress-achievements-list"
                className="flex flex-wrap gap-2"
              >
                {stageCerts.map((c) => (
                  <li
                    key={c.id || `${c.stage_slug}-${c.issued_at}`}
                    data-testid={`achievement-cert-${c.stage_slug}`}
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 px-3 py-1.5 text-xs font-bold"
                  >
                    <Award className="w-3.5 h-3.5" aria-hidden />
                    {c.stage_label || c.stage_slug}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* 4) Escape hatch to the legacy dense dashboard.
            Sunset-gated (§23 Step 3, May 2026): hidden when
            REACT_APP_ENABLE_LEGACY_PROGRESS is set to "0". Default
            ENABLED preserves today's pupil-visible behaviour. */}
        {LEGACY_PROGRESS_ENABLED && (
        <button
          data-testid="open-progress-legacy"
          onClick={() => nav("/learn/progress-legacy")}
          className="w-full flex items-center justify-between rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 hover:bg-slate-50 dark:hover:bg-slate-700 active:scale-[0.99]"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-none">
              <BookOpen className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </div>
            <div className="text-left">
              <p className="text-sm font-extrabold text-slate-900 dark:text-white">
                Detailed lesson dashboard
              </p>
              <p className="text-[12px] text-slate-500 dark:text-slate-400">
                Modules, streaks, and lesson-by-lesson progress.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </button>
        )}
      </div>

      <LearnerBottomNav active="progress" />
    </div>
  );
}
